// src/components/InfoSection.tsx
import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, useInView, animate } from "framer-motion";
import { Stethoscope, Smile, Building2, Sparkles, type LucideIcon } from "lucide-react";
import { useTilt } from "../utils/useTilt";

// Kartlar biraz "yalnız" duruyordu - her istatistiğe kısa bir ikon ekleyip
// bandı daha canlı/okunur hale getiriyoruz.
const infoBoxes: { titleKey: string; textKey: string; icon: LucideIcon }[] = [
  { titleKey: "info.doctor", textKey: "info.doctorCount", icon: Stethoscope },
  { titleKey: "info.happyTreatments", textKey: "info.happyTreatmentsCount", icon: Smile },
  { titleKey: "info.activeClinics", textKey: "info.activeClinicsCount", icon: Building2 },
  { titleKey: "info.services", textKey: "info.servicesCount", icon: Sparkles },
];

// "4+" veya "14000+" gibi metinlerden sayıyı ayırıp 0'dan hedefe sayan animasyon
const CountUpStat: React.FC<{ value: string }> = ({ value }) => {
  const match = value.match(/^(\d+)(.*)$/);
  const target = match ? parseInt(match[1], 10) : 0;
  const suffix = match ? match[2] : "";
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    const controls = animate(0, target, {
      duration: 1.6,
      ease: "easeOut",
      onUpdate(v) {
        setDisplay(Math.floor(v));
      },
    });
    return () => controls.stop();
  }, [isInView, target]);

  return (
    <span ref={ref}>
      {display.toLocaleString("en-US")}
      {suffix}
    </span>
  );
};

const StatCard: React.FC<{ titleKey: string; textKey: string; icon: LucideIcon; idx: number }> = ({
  titleKey,
  textKey,
  icon: Icon,
  idx,
}) => {
  const { t } = useTranslation();
  const { tilt, onMouseMove, onMouseLeave } = useTilt(10);

  return (
    <motion.div
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      animate={{ rotateX: tilt.rotateX, rotateY: tilt.rotateY }}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{
        rotateX: { type: "spring", stiffness: 200, damping: 18 },
        rotateY: { type: "spring", stiffness: 200, damping: 18 },
        default: { duration: 0.5, delay: idx * 0.1 },
      }}
      style={{ transformStyle: "preserve-3d", transformPerspective: 800 }}
      className="relative bg-gradient-to-b from-yellow-50 to-white shadow-md hover:shadow-2xl transition-shadow duration-300 p-8 rounded-2xl flex flex-col items-center text-center border border-yellow-100"
    >
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100 text-yellow-600">
        <Icon size={24} strokeWidth={2} />
      </div>
      <h3 className="text-4xl md:text-5xl font-extrabold mb-2 text-yellow-600">
        <CountUpStat value={t(textKey)} />
      </h3>
      <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider">
        {t(titleKey)}
      </p>
    </motion.div>
  );
};

const InfoSection: React.FC = () => {
  return (
    <section className="bg-white max-w-7xl mx-auto px-6 py-16" style={{ perspective: 1000 }}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
        {infoBoxes.map((box, idx) => (
          <StatCard key={idx} idx={idx} titleKey={box.titleKey} textKey={box.textKey} icon={box.icon} />
        ))}
      </div>
    </section>
  );
};

export default InfoSection;
